import { Box, Stack, MenuList, Typography, styled } from '@mui/material'
import { ArrowRightIcon } from '$common/icons'
import {
  SIMPLE_ITEM_HEIGHT,
  ITEM_WITH_ICON_HEIGHT,
  useSelectMenu,
} from './select-menu.hooks'
import { SelectMenuOptionSkeleton } from './select-menu-option-skeleton'
import { SelectMenuOption } from './select-menu-option'
import { SelectMenuProps } from './select-menu.types'

const HEADER_HEIGHT = 48

const Header = styled(Stack)`
  width: fit-content;
  margin: 10px 15px 10px 0;

  flex-shrink: 0;
  flex-direction: row;
  align-items: center;
`

interface ListContainerProps {
  height: number
  loading: boolean
}

const ListContainer = styled(Box)<ListContainerProps>`
  height: ${props => props.height};
  flex-grow: 1;
  overflow: hidden ${props => (props.loading ? 'hidden' : 'auto')};

  transition: height 300ms;
`

const HeaderIconContainer = styled(Stack)(({ theme }) => ({
  marginLeft: 5,
  color: theme.palette.primary.main,
}))

export function SelectMenu({
  headerTexts = [],
  getHeaderText = (depth: number, selectedOptions: string[]) =>
    selectedOptions[depth - 1],
  HeaderIcon,
  optionFetchers,
  iconFetchers,
  alignOptionsCenter = false,
  maxHeight,
  onDone,
  onExit,
  optionStatusFetchers,
}: SelectMenuProps) {
  const {
    isFirstStep,
    isLastDepth,
    options,
    icons,
    optionsStatus,
    loading,
    headerText,
    selectMenuHeight,
    onOptionClick,
    onBackClick,
  } = useSelectMenu({
    headerTexts,
    getHeaderText,
    optionFetchers,
    iconFetchers,
    maxHeight: maxHeight - HEADER_HEIGHT,
    onDone,
    optionStatusFetchers,
  })

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <Header
        onClick={() => (isFirstStep ? onExit?.() : onBackClick())}
        sx={{ cursor: isFirstStep && !onExit ? 'default' : 'pointer' }}
      >
        <HeaderIconContainer>
          {onExit || !isFirstStep ? (
            <ArrowRightIcon sx={{ color: 'primary.main' }} />
          ) : HeaderIcon ? (
            <HeaderIcon />
          ) : (
            ''
          )}
        </HeaderIconContainer>
        <Typography color="primary" variant="subtitle1">
          {headerText}
        </Typography>
      </Header>

      <ListContainer height={selectMenuHeight} loading={loading}>
        <MenuList disablePadding>
          {options?.map((value, index) =>
            loading ? (
              <SelectMenuOptionSkeleton
                key={value + index}
                alignedCenter={alignOptionsCenter}
              />
            ) : (
              <SelectMenuOption
                value={value}
                key={value}
                icon={icons[index]}
                isLeaf={isLastDepth && !onExit}
                alignedCenter={alignOptionsCenter}
                onClick={option => onOptionClick(option, index)}
                height={
                  icons[index] ? ITEM_WITH_ICON_HEIGHT : SIMPLE_ITEM_HEIGHT
                }
                disabled={optionsStatus[index]?.disabled}
                badge={optionsStatus[index]?.badge}
              />
            ),
          )}
        </MenuList>
      </ListContainer>
    </Box>
  )
}
